import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { EyeTrackingHealthSeverity } from "@utils/eyeTrackingHealthUtils";

// Connection states for WebSocket
export enum WebSocketConnectionState {
  DISCONNECTED = "disconnected",
  CONNECTING = "connecting",
  CONNECTED = "connected",
  RECONNECTING = "reconnecting",
}

// Connection states for WebRTC
export enum WebRTCConnectionState {
  DISCONNECTED = "disconnected",
  CONNECTING = "connecting",
  CONNECTED = "connected",
  FAILED = "failed",
}

// Delivery receipt for the latest test start/command sent to the headset.
// 'pending'   — sent, no receipt yet (also the only state a legacy backend produces)
// 'delivered' — backend wrote it to the headset socket
// 'duplicate' — backend absorbed a retry of an already-delivered start
// 'acked'     — headset explicitly confirmed receipt (new headset builds only)
// 'failed'    — headset offline; the command was not delivered
export type StartDeliveryState =
  | "pending"
  | "delivered"
  | "duplicate"
  | "acked"
  | "failed"
  | "rejected";

// Receipt state is monotonic per start uuid: retries and flapped sockets
// produce late, weaker receipts (a relay-failed for a redundant retry after
// the headset already acked, or a retry's 'pending' after 'delivered'), and a
// downgrade would abort a start the device may already have. 'failed' only
// beats 'pending'; a different uuid is a new attempt and always replaces.
const START_DELIVERY_RANK: Record<StartDeliveryState, number> = {
  pending: 0,
  failed: 1,
  delivered: 2,
  duplicate: 3,
  acked: 4,
  rejected: 5,
};

export interface StartDeliveryReceipt {
  uuid: string | null;
  state: StartDeliveryState | null;
  reason: string | null;
  updatedAt: number | null;
}

export interface SystemOverlayStatus {
  open: boolean;
  reason: string | null;
  scene: string | null;
  dismissed: boolean;
  updatedAt: number | null;
}

export interface EyeTrackingFailure {
  startUuid: string | null;
  recoveryAttempts: number | null;
  scene: string | null;
  reason: string | null;
  severity: EyeTrackingHealthSeverity;
  reportedAt: number;
}

export interface ConnectionState {
  websocket: {
    state: WebSocketConnectionState;
    isOpen: boolean;
    lastConnectAttempt: number | null;
    reconnectAttempts: number;
    // Round-trip time of the last ping/pong with the backend (null until the
    // backend supports pong echoes).
    lastRttMs: number | null;
    rttUpdatedAt: number | null;
  };
  startDelivery: StartDeliveryReceipt;
  systemOverlay: SystemOverlayStatus;
  eyeTrackingFailure: EyeTrackingFailure | null;
  webrtc: {
    state: WebRTCConnectionState;
    isDataChannelOpen: boolean;
    lastOffer: number | null;
    lastError: string | null;
  };
}

const initialState: ConnectionState = {
  websocket: {
    state: WebSocketConnectionState.DISCONNECTED,
    isOpen: false,
    lastConnectAttempt: null,
    reconnectAttempts: 0,
    lastRttMs: null,
    rttUpdatedAt: null,
  },
  startDelivery: {
    uuid: null,
    state: null,
    reason: null,
    updatedAt: null,
  },
  systemOverlay: {
    open: false,
    reason: null,
    scene: null,
    dismissed: false,
    updatedAt: null,
  },
  eyeTrackingFailure: null,
  webrtc: {
    state: WebRTCConnectionState.DISCONNECTED,
    isDataChannelOpen: false,
    lastOffer: null,
    lastError: null,
  },
};

const TEST_RUN_CHANGE_ACTIONS: ReadonlySet<string> = new Set([
  "test/setTestId",
  "test/resetTestState",
  "testSuite/setActiveTest",
  "testSuite/moveToNextTest",
  "testSuite/clearTestSuite",
]);

export const connectionSlice = createSlice({
  name: "connection",
  initialState,
  reducers: {
    // WebSocket Actions
    setWebSocketState(state, action: PayloadAction<WebSocketConnectionState>) {
      state.websocket.state = action.payload;
      if (action.payload === WebSocketConnectionState.CONNECTED) {
        state.websocket.isOpen = true;
        state.websocket.reconnectAttempts = 0;
      } else if (
        action.payload === WebSocketConnectionState.DISCONNECTED ||
        action.payload === WebSocketConnectionState.RECONNECTING
      ) {
        state.websocket.isOpen = false;
        state.systemOverlay = { ...initialState.systemOverlay };
      }
    },

    incrementReconnectAttempts(state) {
      state.websocket.reconnectAttempts += 1;
    },

    setWebSocketOpen(state, action: PayloadAction<boolean>) {
      state.websocket.isOpen = action.payload;
      if (action.payload) {
        state.websocket.state = WebSocketConnectionState.CONNECTED;
      } else {
        if (state.websocket.state === WebSocketConnectionState.CONNECTED) {
          state.websocket.state = WebSocketConnectionState.DISCONNECTED;
        }
        state.systemOverlay = { ...initialState.systemOverlay };
      }
    },

    attemptWebSocketConnect(state) {
      state.websocket.lastConnectAttempt = Date.now();
      state.websocket.state = WebSocketConnectionState.CONNECTING;
    },

    requestWebSocketPing() {
      // No state change — handled in connectionSagas, which sends a ping so a
      // fresh RTT sample arrives without waiting for the 10 s ping interval.
    },

    resetWebSocketConnection(state) {
      state.websocket = {
        ...initialState.websocket,
        lastConnectAttempt: Date.now(),
      };
      state.systemOverlay = { ...initialState.systemOverlay };
    },

    setWebSocketRtt(state, action: PayloadAction<number>) {
      state.websocket.lastRttMs = action.payload;
      state.websocket.rttUpdatedAt = Date.now();
    },

    setStartDeliveryReceipt(
      state,
      action: PayloadAction<{
        uuid: string | null;
        state: StartDeliveryState;
        reason?: string | null;
      }>,
    ) {
      const current = state.startDelivery;
      if (
        current.uuid !== null &&
        current.uuid === action.payload.uuid &&
        current.state !== null &&
        START_DELIVERY_RANK[action.payload.state] <= START_DELIVERY_RANK[current.state]
      ) {
        return;
      }
      state.startDelivery = {
        uuid: action.payload.uuid,
        state: action.payload.state,
        reason: action.payload.reason ?? null,
        updatedAt: Date.now(),
      };
    },

    clearStartDelivery(state) {
      state.startDelivery = { ...initialState.startDelivery };
    },

    setSystemOverlayStatus(
      state,
      action: PayloadAction<{ open: boolean; reason?: string | null; scene?: string | null }>,
    ) {
      // A repeated open notification must not resurrect a warning the clinician dismissed.
      const staysOpen = state.systemOverlay.open && action.payload.open;
      state.systemOverlay = {
        open: action.payload.open,
        reason: action.payload.reason ?? null,
        scene: action.payload.scene ?? null,
        dismissed: staysOpen ? state.systemOverlay.dismissed : false,
        updatedAt: Date.now(),
      };
    },

    dismissSystemOverlayWarning(state) {
      state.systemOverlay.dismissed = true;
    },

    setEyeTrackingFailure(state, action: PayloadAction<Omit<EyeTrackingFailure, "reportedAt">>) {
      state.eyeTrackingFailure = { ...action.payload, reportedAt: Date.now() };
    },

    clearEyeTrackingFailure(state) {
      state.eyeTrackingFailure = null;
    },

    // WebRTC Actions
    setWebRTCState(state, action: PayloadAction<WebRTCConnectionState>) {
      state.webrtc.state = action.payload;
      if (action.payload === WebRTCConnectionState.CONNECTED) {
        state.webrtc.isDataChannelOpen = true;
      } else if (
        action.payload === WebRTCConnectionState.DISCONNECTED ||
        action.payload === WebRTCConnectionState.FAILED
      ) {
        state.webrtc.isDataChannelOpen = false;
      }
    },

    setWebRTCDataChannelOpen(state, action: PayloadAction<boolean>) {
      state.webrtc.isDataChannelOpen = action.payload;
      if (action.payload) {
        state.webrtc.state = WebRTCConnectionState.CONNECTED;
      } else if (state.webrtc.state === WebRTCConnectionState.CONNECTED) {
        state.webrtc.state = WebRTCConnectionState.DISCONNECTED;
      }
    },

    sendOffer(state) {
      state.webrtc.lastOffer = Date.now();
      state.webrtc.state = WebRTCConnectionState.CONNECTING;
    },

    setWebRTCError(state, action: PayloadAction<string>) {
      state.webrtc.lastError = action.payload;
      state.webrtc.state = WebRTCConnectionState.FAILED;
    },

    resetWebRTCConnection(state) {
      state.webrtc = {
        ...initialState.webrtc,
      };
    },

    // Reset all connection states
    resetAllConnections(_state) {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // WsUtil drops an unhealthy report whose severity already matches the banner on screen, so
    // this reset is what keeps a prior run's startUuid from stranding one that cannot be cleared.
    builder.addMatcher(
      (action) => TEST_RUN_CHANGE_ACTIONS.has((action as { type: string }).type),
      (state) => {
        state.eyeTrackingFailure = null;
      },
    );
  },
});

export const {
  // WebSocket actions
  setWebSocketState,
  incrementReconnectAttempts,
  setWebSocketOpen,
  attemptWebSocketConnect,
  requestWebSocketPing,
  resetWebSocketConnection,
  setWebSocketRtt,
  setStartDeliveryReceipt,
  clearStartDelivery,
  setSystemOverlayStatus,
  dismissSystemOverlayWarning,
  setEyeTrackingFailure,
  clearEyeTrackingFailure,

  // WebRTC actions
  setWebRTCState,
  setWebRTCDataChannelOpen,
  sendOffer,
  setWebRTCError,
  resetWebRTCConnection,

  // Common actions
  resetAllConnections,
} = connectionSlice.actions;

export default connectionSlice.reducer;
